import React, { useState } from 'react';

/**
 * Shared contact form API and submit logic.
 * Use this on every page that shows the contact form so all forms save to the same backend.
 *
 * Backend: run from project root: cd server && npm install && npm start (port 3002)
 */
export const CONTACT_API_URL = 'http://localhost:3002/api/contact';

const defaultErrorMessage = 'Something went wrong. Please try again or email us directly.';

/**
 * Submits contact form data to the backend. Returns a Promise that resolves to
 * { success: true } on 201, or { success: false } otherwise / on network error.
 * @param {object} formData - { name, email, phone, message }
 * @param {object} [options] - { formSource: 'popup' | 'page' } so backend can store where the submission came from
 */
export function submitContactForm(formData, options = {}) {
  if (!CONTACT_API_URL) {
    return Promise.resolve({ success: true });
  }
  const payload = {
    name: String(formData.name ?? '').trim(),
    email: String(formData.email ?? '').trim(),
    phone: String(formData.phone ?? '').trim(),
    message: String(formData.message ?? '').trim(),
    formSource: options.formSource === 'popup' ? 'popup' : 'page',
    ...(formData.organisation !== undefined && { organisation: String(formData.organisation ?? '').trim() }),
  };
  return fetch(CONTACT_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((res) =>
      res.json().then((data) => ({ status: res.status, data })).catch(() => ({ status: res.status, data: {} }))
    )
    .then(({ status, data }) => (status === 201 && data.success ? { success: true } : { success: false }))
    .catch(() => ({ success: false }));
}

/**
 * Hook for pages that render the contact Form. Returns submitStatus, handleSubmit, and errorMessage
 * so the same form behavior (backend save + success/error) is consistent everywhere.
 * @param {string} [errorMessage] - Optional custom error message.
 */
export function useContactForm(errorMessage = defaultErrorMessage) {
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleSubmit = (formData) => {
    setSubmitStatus(null);
    submitContactForm(formData).then(({ success }) => {
      setSubmitStatus(success ? 'success' : 'error');
    });
  };

  return { submitStatus, handleSubmit, errorMessage };
}
